import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import API from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

// The reusable "proper flow" engine every MCQ topic (Percentages, Time & Work, ...)
// is built on: pick an option -> instant feedback -> step-by-step official solution
// -> common trap -> an official shortcut method -> community-contributed methods.
// Question content itself lives in data/aptitudeQuestions.js, hand-verified per entry.

const DIFFICULTY_TOKEN = { Easy: 'var(--success)', Medium: 'var(--warning)', Hard: 'var(--danger)' };
const STEP_LABELS_ICON = { Given: '01', Concept: '02', Working: '03', Answer: '04' };

const LockIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);
const BoltIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 2 3 14h6l-1 8 10-12h-6l1-8Z" />
  </svg>
);
const WarningIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3 2 20h20L12 3Z" /><path d="M12 9.5v4.2" /><path d="M12 17h.01" />
  </svg>
);
const ChatIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 5.5A2 2 0 0 1 6 3.5h12a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H9l-4 4v-4H6a2 2 0 0 1-2-2v-9Z" />
  </svg>
);

// Numbered stepper — the four solution steps are genuinely sequential, so a
// progress track (not decorative dots) carries real information here.
const StepTrack = ({ labels, active }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start' }}>
    {labels.map((label, i) => (
      <React.Fragment key={label}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 7, minWidth: 34 }}>
          <span style={{
            width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 10.5, fontWeight: 800, flexShrink: 0,
            background: i <= active ? 'var(--v1)' : 'transparent',
            color: i <= active ? '#fff' : 'var(--muted)',
            border: `1.5px solid ${i <= active ? 'var(--v1)' : 'var(--border-strong)'}`,
          }}>{STEP_LABELS_ICON[label] || i + 1}</span>
          <span style={{
            fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'center',
            color: i === active ? 'var(--text)' : 'var(--muted)',
          }}>{label}</span>
        </div>
        {i < labels.length - 1 && (
          <div style={{ flex: 1, height: 1.5, marginTop: 12, background: i < active ? 'var(--v1)' : 'var(--border)' }} />
        )}
      </React.Fragment>
    ))}
  </div>
);

const QuestionFlow = ({ question }) => {
  const { user } = useAuth();
  const [selected, setSelected] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [showAlt, setShowAlt] = useState(false);
  const [answerText, setAnswerText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [justSubmitted, setJustSubmitted] = useState(false);
  const [community, setCommunity] = useState([]);
  const [loadingCommunity, setLoadingCommunity] = useState(true);

  useEffect(() => {
    setSelected(null);
    setStepIndex(0);
    setShowAlt(false);
    setAnswerText('');
    setJustSubmitted(false);
    setLoadingCommunity(true);
    API.get(`/aptitude/submissions/${question.id}`)
      .then(({ data }) => setCommunity(data))
      .catch(() => setCommunity([]))
      .finally(() => setLoadingCommunity(false));
  }, [question.id]);

  const answered = selected !== null;
  const isCorrect = selected === question.correctIndex;
  const onLastStep = stepIndex === question.steps.length - 1;
  const stepLabels = question.steps.map(s => s.label);

  const handleSubmitMethod = async e => {
    e.preventDefault();
    if (answerText.trim().length < 10) {
      toast.error('Add a bit more detail before submitting.');
      return;
    }
    setSubmitting(true);
    try {
      await API.post('/aptitude/submissions', {
        questionId: question.id,
        topic: question.topic,
        answerText: answerText.trim(),
      });
      toast.success('Submitted! It\'ll appear here once reviewed.');
      setAnswerText('');
      setJustSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not submit right now.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="apt2-grid">
      {/* ── Left: the question itself ── */}
      <div className="apt2-card apt2-card-question">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          <span style={{
            padding: '4px 12px', borderRadius: 6, fontSize: 11.5, fontWeight: 800,
            textTransform: 'uppercase', letterSpacing: '0.04em',
            color: DIFFICULTY_TOKEN[question.difficulty] || 'var(--warning)',
            background: 'var(--bg3)', border: `1px solid ${DIFFICULTY_TOKEN[question.difficulty] || 'var(--warning)'}`,
          }}>{question.difficulty}</span>
          {question.patternTags?.map(tag => (
            <span key={tag} className="apt2-tagchip">{tag}</span>
          ))}
        </div>

        {question.table && (
          <div style={{ overflowX: 'auto', marginBottom: 18, borderRadius: 12, border: '1px solid var(--border)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
              <thead>
                <tr style={{ background: 'var(--bg3)' }}>
                  {question.table.headers.map((h, i) => (
                    <th key={i} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 800, color: 'var(--text)', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {question.table.rows.map((row, ri) => (
                  <tr key={ri} style={{ borderBottom: ri < question.table.rows.length - 1 ? '1px solid var(--border)' : 'none' }}>
                    {row.map((cell, ci) => (
                      <td key={ci} style={{ padding: '9px 14px', color: 'var(--text-2)', whiteSpace: 'nowrap' }}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--text)', fontWeight: 600, marginBottom: 22 }}>
          {question.question}
        </p>

        <div style={{ display: 'grid', gap: 10 }}>
          {question.options.map((opt, i) => {
            const isPicked = selected === i;
            const isRightOpt = i === question.correctIndex;
            let border = 'var(--border-strong)', badgeBg = 'var(--bg3)', badgeColor = 'var(--muted)', textColor = 'var(--text)';
            if (answered && isRightOpt) { border = 'var(--success)'; badgeBg = 'var(--success)'; badgeColor = '#fff'; textColor = 'var(--success)'; }
            else if (answered && isPicked && !isRightOpt) { border = 'var(--danger)'; badgeBg = 'var(--danger)'; badgeColor = '#fff'; textColor = 'var(--danger)'; }
            return (
              <button
                key={i}
                disabled={answered}
                onClick={() => setSelected(i)}
                className="apt2-option"
                style={{ borderColor: border, cursor: answered ? 'default' : 'pointer' }}
              >
                <span className="apt2-option-badge" style={{ background: badgeBg, color: badgeColor }}>
                  {String.fromCharCode(65 + i)}
                </span>
                <span style={{ fontSize: 14.5, fontWeight: 600, color: textColor }}>{opt}</span>
                {answered && isRightOpt && <span className="apt2-option-mark" style={{ color: 'var(--success)' }}>✓</span>}
                {answered && isPicked && !isRightOpt && <span className="apt2-option-mark" style={{ color: 'var(--danger)' }}>✗</span>}
              </button>
            );
          })}
        </div>

        {answered && (
          <div className="apt2-feedback" style={{
            color: isCorrect ? 'var(--success)' : 'var(--danger)',
            borderColor: isCorrect ? 'var(--success)' : 'var(--danger)',
          }}>
            {isCorrect ? 'Correct — see the full working on the right.' : 'Not quite — walk through the solution on the right to see where it turns.'}
          </div>
        )}
      </div>

      {/* ── Right: solution, once answered ── */}
      {!answered ? (
        <div className="apt2-card apt2-card-locked">
          <LockIcon />
          <p style={{ fontSize: 13.5, color: 'var(--muted)', lineHeight: 1.6, margin: '10px 0 0', maxWidth: 240 }}>
            Pick an answer to unlock the step-by-step solution, the shortcut method, and community answers.
          </p>
        </div>
      ) : (
        <div className="apt2-card apt2-card-solution">
          <div className="apt2-stepper-wrap">
            <StepTrack labels={stepLabels} active={stepIndex} />
          </div>
          <p style={{ fontSize: 14.5, lineHeight: 1.75, color: 'var(--text)', margin: '18px 0', minHeight: 66 }}>
            {question.steps[stepIndex].text}
          </p>
          <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
            <button onClick={() => setStepIndex(i => Math.max(0, i - 1))} disabled={stepIndex === 0} className="apt2-navbtn">← Back</button>
            <button onClick={() => setStepIndex(i => Math.min(question.steps.length - 1, i + 1))} disabled={onLastStep} className="apt2-navbtn apt2-navbtn-primary">Next →</button>
          </div>

          {onLastStep && (
            <div className="apt2-trap">
              <span style={{ color: 'var(--warning)', flexShrink: 0, marginTop: 1 }}><WarningIcon /></span>
              <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.65, color: 'var(--text)' }}>
                <strong style={{ color: 'var(--warning)' }}>Common trap — </strong>{question.commonTrap}
              </p>
            </div>
          )}

          {question.alternateMethod && (
            <div style={{ marginTop: 18 }}>
              <button onClick={() => setShowAlt(s => !s)} className="apt2-altbtn" data-open={showAlt}>
                <BoltIcon /> {showAlt ? 'Hide' : 'Show'} alternate method — {question.alternateMethod.label}
              </button>
              {showAlt && (
                <ol className="apt2-altsteps">
                  {question.alternateMethod.steps.map((s, i) => <li key={i}>{s}</li>)}
                </ol>
              )}
            </div>
          )}

          <div className="apt2-community">
            <h4 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 800, marginBottom: 14, color: 'var(--text)' }}>
              <ChatIcon /> Community optimized answers
            </h4>

            {loadingCommunity ? (
              <p style={{ fontSize: 13, color: 'var(--muted)' }}>Loading…</p>
            ) : community.length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>No community methods yet — be the first to share a faster way.</p>
            ) : (
              <div style={{ display: 'grid', gap: 10, marginBottom: 16 }}>
                {community.map(c => (
                  <div key={c._id} className="apt2-commentcard">
                    <p style={{ margin: '0 0 6px', fontSize: 13.5, lineHeight: 1.65, color: 'var(--text)', whiteSpace: 'pre-wrap' }}>{c.answerText}</p>
                    <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--muted)' }}>— {c.name}</span>
                  </div>
                ))}
              </div>
            )}

            {user ? (
              justSubmitted ? (
                <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--success)' }}>Submitted — pending review, thanks for sharing!</p>
              ) : (
                <form onSubmit={handleSubmitMethod}>
                  <textarea
                    value={answerText}
                    onChange={e => setAnswerText(e.target.value)}
                    placeholder="Got a quicker or different way to solve this? Describe it here…"
                    rows={3}
                    className="apt2-textarea"
                  />
                  <button type="submit" disabled={submitting} className="apt2-navbtn apt2-navbtn-primary" style={{ opacity: submitting ? 0.7 : 1 }}>
                    {submitting ? 'Submitting…' : 'Submit my method'}
                  </button>
                </form>
              )
            ) : (
              <p style={{ fontSize: 13, color: 'var(--muted)' }}>
                <a href="/login" style={{ color: 'var(--v1)', fontWeight: 700, textDecoration: 'none' }}>Log in</a> to share your own optimized answer.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionFlow;
